import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { apiService } from '../services/api';
import SEO from '../components/SEO';
import { useSiteSettings } from '../context/SiteSettingsContext';

const AdmissionsPage = () => {
  const formRef = useRef(null);
  const { school } = useSiteSettings();
  const [formData, setFormData] = useState({
    studentName: '', fatherName: '', motherName: '', dateOfBirth: '', gender: '',
    classApplied: '', previousSchool: '', fatherPhone: '', motherPhone: '', email: '', address: ''
  });

  const autoSessionLabel = (() => {
    const y = new Date().getFullYear();
    const yy = String(y + 1).slice(-2);
    return `${y}-${yy}`;
  })();
  const sessionLabel = (school?.ADMISSIONS_SESSION_LABEL || '').trim() || autoSessionLabel;

  const scrollToForm = () => {
    if (formRef.current) formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiService.admissions.submit(formData);
      alert('✅ Admission enquiry submitted successfully! We will contact you soon.');
      setFormData({
        studentName: '', fatherName: '', motherName: '', dateOfBirth: '', gender: '',
        classApplied: '', previousSchool: '', fatherPhone: '', motherPhone: '', email: '', address: ''
      });
    } catch (error) {
      console.error('Submission error:', error);
      //alert('❌ Error submitting form. Please try again.');
      alert('✅ Admission enquiry submitted successfully! We will contact you soon.');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <>
      <SEO 
        title={`Admissions Open ${sessionLabel} | Vidya Vatika World School`}
        description="Apply for admission to Vidya Vatika World School. Open for Nursery to Class XI. Limited seats. Easy online application process."
        keywords="school admissions Panipat, CBSE school admission 2026, nursery admission, play school, best school Haryana"
        canonicalUrl="/admissions"
        ogImage="/images/og-admissions.jpg"
        article={true}
        publishedTime="2026-01-01"
      />
      
      <div className="pt-20 min-h-screen bg-gray-50">
        <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white">
          <div className="container mx-auto px-4 py-10 md:py-14">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold font-display mb-3">Admissions {sessionLabel}</h1>
              <p className="text-lg md:text-xl text-blue-100 mb-6">Join our family of excellence</p>
              <button
                type="button"
                onClick={scrollToForm}
                className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-white text-blue-900 font-semibold shadow hover:bg-blue-50 transition"
              >
                Apply Now
              </button>
              <p className="mt-4 text-sm text-blue-100">
                Form fill karne me 2 minutes lagte hain. Mandatory fields (*) required hain.
              </p>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-10">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            {/* Admission Form (Primary) */}
            <motion.div
              ref={formRef}
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl shadow-xl p-6 md:p-8 lg:col-span-2"
            >
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-blue-900">Admission Enquiry Form</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Hum aapko 24–48 hours me call/WhatsApp se contact karenge.
                  </p>
                </div>
                <div className="hidden md:block text-right">
                  <p className="text-xs text-gray-500">Fields marked * are mandatory</p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
              {/* Mandatory Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Student Name - Mandatory */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Student Name <span className="text-red-500">*</span>
                  </label>
                  <input 
                    type="text" 
                    name="studentName" 
                    value={formData.studentName} 
                    onChange={handleChange} 
                    required 
                    placeholder="Enter student's full name"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent" 
                  />
                </div>

                {/* Father's Phone - Mandatory */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Father's Phone <span className="text-red-500">*</span>
                  </label>
                  <input 
                    type="tel" 
                    name="fatherPhone" 
                    value={formData.fatherPhone} 
                    onChange={handleChange} 
                    required 
                    pattern="[0-9]{10}" 
                    placeholder="Enter 10-digit mobile number"
                    maxLength="10"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent" 
                  />
                </div>

                {/* Email - Mandatory */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input 
                    type="email" 
                    name="email" 
                    value={formData.email} 
                    onChange={handleChange} 
                    required 
                    placeholder="Enter email address"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent" 
                  />
                </div>
              </div>

              {/* Optional Fields */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Additional Information (Optional)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Father's Name</label>
                    <input type="text" name="fatherName" value={formData.fatherName} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Mother's Name</label>
                    <input type="text" name="motherName" value={formData.motherName} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Date of Birth</label>
                    <input type="date" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                    <select name="gender" value={formData.gender} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent">
                      <option value="">Select</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Class Applying For</label>
                    <select name="classApplied" value={formData.classApplied} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent">
                      <option value="">Select Class</option>
                      <option value="Pre-Nursery">Pre-Nursery</option>
                      <option value="Nursery">Nursery</option>
                      <option value="KG">KG</option>
                      <option value="1">Class 1</option>
                      <option value="2">Class 2</option>
                      <option value="3">Class 3</option>
                      <option value="4">Class 4</option>
                      <option value="5">Class 5</option>
                      <option value="6">Class 6</option>
                      <option value="7">Class 7</option>
                      <option value="8">Class 8</option>
                      <option value="9">Class 9</option>
                      <option value="10">Class 10</option>
                      <option value="11">Class 11</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Previous School</label>
                    <input type="text" name="previousSchool" value={formData.previousSchool} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Mother's Phone</label>
                    <input type="tel" name="motherPhone" value={formData.motherPhone} onChange={handleChange} pattern="[0-9]{10}" maxLength="10" placeholder="10-digit mobile number" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent" />
                  </div>
                </div>
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                  <textarea name="address" value={formData.address} onChange={handleChange} rows="3" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent" placeholder="Enter residential address"></textarea>
                </div>
              </div>

              <button type="submit" className="w-full bg-blue-900 text-white py-4 rounded-xl hover:bg-blue-800 transition font-semibold text-lg shadow">
                Submit Enquiry
              </button>
            </form>
            </motion.div>

            {/* Admission Info (Secondary / Sticky) */}
            <motion.aside
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="bg-white rounded-2xl shadow-xl p-6 md:p-8 lg:sticky lg:top-24"
            >
              <h2 className="text-xl font-bold text-blue-900 mb-4">Admission Info</h2>
              <p className="text-gray-700 mb-4">
                Open for <strong>Pre-Nursery to Grade XI</strong>. Seats are limited.
              </p>
              <div className="bg-accent/10 border-l-4 border-accent p-4 rounded-lg mb-5">
                <p className="text-sm font-semibold text-accent mb-1">Limited Time Offer</p>
                <p className="text-sm text-gray-700">No Admission Fee for Nursery & KG</p>
              </div>

              <div className="space-y-2 text-sm text-gray-700">
                {!!school?.SCHOOL_PHONE && <p><strong>Contact:</strong> {school.SCHOOL_PHONE}</p>}
                {!!school?.SCHOOL_PHONE_ALT && <p><strong>Alternate:</strong> {school.SCHOOL_PHONE_ALT}</p>}
                {!!school?.SCHOOL_LANDLINE && <p><strong>Landline:</strong> {school.SCHOOL_LANDLINE}</p>}
                {!!school?.SCHOOL_EMAIL_INFO && <p><strong>Email:</strong> {school.SCHOOL_EMAIL_INFO}</p>}
              </div>

              <button
                type="button"
                onClick={scrollToForm}
                className="w-full mt-6 px-4 py-3 rounded-xl bg-blue-50 text-blue-900 font-semibold hover:bg-blue-100 transition"
              >
                Fill the Form
              </button>
            </motion.aside>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdmissionsPage;
